import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { queryKeys } from 'shared/api/queryKeys';
import { getConnectionDetails } from 'connections/api/connectionsApi';
import { updateConnection } from 'routes/api';
import { trackDestinationUpdated } from 'shared/analytics/analytics';
import { Connection, buildConnection } from 'shared/types/ObjectTypes';
import { loadCurrentUserDetails } from 'workspaces/loadCurrentUserDetails';
import {
  connectionFormErrors,
  connectionPatch,
  connectionTitleFor,
  toFormValues,
} from './connectionForm';

// The Connection record: fetched, copied into an editable form, saved, and
// reloaded after a save elsewhere on the screen.
export function useConnectionRecord({
  connectionId,
  canFetch,
}: {
  connectionId: string | undefined;
  canFetch: boolean;
}) {
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [changesMade, setChangesMade] = useState(false);
  const [currentMemberIsAdmin, setCurrentMemberIsAdmin] =
    useState<boolean>(false);
  const [notFound, setNotFound] = useState(false);
  const [connection, setConnection] = useState<Connection>(buildConnection());
  const [connectionTitle, setConnectionTitle] = useState<string>('');
  const [errors, setErrors] = useState({});

  // The record itself and whether you administer its workspace.
  const detailQuery = useQuery({
    queryKey: queryKeys.connections.detail(connectionId ?? ''),
    // Not kept after leaving the page: a cached copy would fill the form on
    // the next visit and the refetch would then overwrite what the user
    // started typing. Saves still refresh it in place.
    gcTime: 0,
    enabled: canFetch,
    queryFn: async () => {
      const temp = await getConnectionDetails(
        keycloak.token as string,
        connectionId as string,
      );
      if (!temp || !temp.owner || !temp.owner.id) return null;
      const isAdmin = await loadCurrentUserDetails(
        temp.owner.id,
        keycloak.token as string,
      );
      return { record: temp as Connection, isAdmin: !!isAdmin };
    },
  });
  // When the page last copied a fetched record into its editable state, so a
  // caller waiting on a reload knows the form already shows the fresh values.
  const [seededAt, setSeededAt] = useState(0);
  useEffect(() => {
    const result = detailQuery.data;
    if (result === undefined) return;
    if (result === null) {
      setSeededAt(detailQuery.dataUpdatedAt);
      return;
    }
    setCurrentMemberIsAdmin(result.isAdmin);
    setConnectionTitle(connectionTitleFor(result.record));
    // Reset only on success: an eager reset flashes the loading state over
    // the 404 page while a retry runs.
    setNotFound(false);
  }, [detailQuery.data, detailQuery.dataUpdatedAt]);
  // The editable copy follows the server only while nothing is unsaved, so a
  // refresh never wipes edits in progress; once they're saved or discarded it
  // catches up. It also waits out a refresh in flight, so a save never briefly
  // shows the pre-save values.
  useEffect(() => {
    const result = detailQuery.data;
    if (!result || changesMade || detailQuery.isFetching) return;
    setConnection(toFormValues(result.record));
    setSeededAt(detailQuery.dataUpdatedAt);
  }, [
    detailQuery.data,
    detailQuery.dataUpdatedAt,
    changesMade,
    detailQuery.isFetching,
  ]);
  useEffect(() => {
    if (!detailQuery.error) return;
    // 403 is treated like 404: the entity is not viewable by this user
    const status = (detailQuery.error as any)?.response?.status;
    if (status === 404 || status === 403) setNotFound(true);
    else console.log('Failed to load destination details:', detailQuery.error);
  }, [detailQuery.error, detailQuery.errorUpdatedAt]);

  // The details answer, keyed to the URL. NOT about stale records from a
  // previous id — an id change remounts the screen (key={dest_id} in App.tsx),
  // resetting `connection`. What this marks is the window where `connection` is
  // still the empty placeholder (details pending, failed, or the record came
  // back without an owner): role ACTIONS must not be offered then, because they
  // would create or attach with an empty id and owner.
  const connectionLoaded = !!connectionId && connection.id === connectionId;

  // Refetch the record and everything loaded about it (its Routes, its
  // provider role) after a save.
  const reload = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.connections.byId(connectionId ?? ''),
    });
  // Callers that need to KNOW when the reload finished (the configuration
  // editor, which drops its unsaved-edit stash only once the fetched record
  // carries the saved values — otherwise the form flashes the pre-save value).
  // The flag alone cannot tell them: setting it returns immediately.
  const pendingReloadWaiters = useRef<{ since: number; resolve: () => void }[]>(
    [],
  );
  // Resolves once a record fetched after the request has been copied into the
  // form, or the refetch failed — and at once when no refetch can run (session
  // expired), with a 15 s backstop so a caller can never wait forever.
  useEffect(() => {
    const settledAt = Math.max(seededAt, detailQuery.errorUpdatedAt);
    // Same millisecond counts as after: a fast refetch can settle in the
    // millisecond the waiter was created.
    const ready = pendingReloadWaiters.current.filter(
      (w) => w.since <= settledAt,
    );
    if (ready.length === 0) return;
    pendingReloadWaiters.current = pendingReloadWaiters.current.filter(
      (w) => w.since > settledAt,
    );
    ready.forEach((w) => w.resolve());
  }, [seededAt, detailQuery.errorUpdatedAt]);
  const reloadAndWait = () =>
    new Promise<void>((resolve) => {
      setChangesMade(false);
      if (!canFetch) return resolve();
      const waiter = { since: Date.now(), resolve };
      pendingReloadWaiters.current.push(waiter);
      setTimeout(() => {
        if (!pendingReloadWaiters.current.includes(waiter)) return;
        pendingReloadWaiters.current = pendingReloadWaiters.current.filter(
          (w) => w !== waiter,
        );
        resolve();
      }, 15000);
      // The save that called this already refreshed the record; only ask
      // again when the record's own refresh isn't still running. Only the
      // record's reload resolves the waiter, so a sibling query still loading
      // (its Routes, its role) doesn't count.
      if (
        queryClient.isFetching({
          queryKey: queryKeys.connections.detail(connectionId ?? ''),
          exact: true,
        }) === 0
      )
        reload();
    });

  const onChangeField = (e: any) => {
    const attr: string = e.target.id;
    setChangesMade(true);
    setConnection((prevConnection: Connection) => ({
      ...prevConnection,
      [attr]: e.target.value,
    }));
  };

  const onChangeOwner = (e: any) => {
    // Re-selecting the workspace the Connection already has (the dropdown does
    // this on its own when the user has a single workspace) changes nothing,
    // so it must not raise the unsaved-changes prompt.
    if (e?.value === connection.owner?.id) return;
    setChangesMade(true);
    setConnection((prevConnection: Connection) => ({
      ...prevConnection,
      owner: {
        name: e.label,
        id: e.value,
        description: '',
      },
    }));
  };

  // Resolves true only when the save landed — the section-switch prompt uses it
  // to decide whether it is safe to move to the section the user asked for.
  const save = async (e: React.FormEvent<Element>): Promise<boolean> => {
    const formErrors = connectionFormErrors(connection, t);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return false;
    if (!keycloak.token) return false;
    try {
      const saved = await updateConnection(
        keycloak.token,
        connection.id,
        connectionPatch(connection),
      );
      // A 401 is swallowed by the API layer (the login modal handles
      // it) and comes back as undefined — that is not a save, so the
      // edits stay put rather than being cleared behind a toast.
      if (!saved) return false;
      toast.success(`${t('Destination updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      trackDestinationUpdated(
        connection.id,
        connection.type?.name,
        connection.owner?.name,
      );
      setChangesMade(false);
      return true;
    } catch (err: any) {
      toast.error(t('Failed to save destination. Please try again.'), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
  };

  return {
    connection,
    connectionTitle,
    connectionLoaded,
    currentMemberIsAdmin,
    notFound,
    hasRecord: !!detailQuery.data,
    changesMade,
    errors,
    onChangeField,
    onChangeOwner,
    save,
    // Drops the unsaved edits and shows the record as stored.
    discardAndReload: () => {
      setChangesMade(false);
      reload();
    },
    reloadAndWait,
  };
}
